/**
 * Verifica pubblica di un attestato dal codice (anche dal QR stampato sul PDF).
 * Mai il codice fiscale: solo nome, cognome, webinar, data, presenza ed esito.
 */
import { formattaCodice, formattaMinuti } from '@aulaviva/shared';
import { Alert, Button, CodiceVerificaField, LUNGHEZZA_CODICE, normalizzaCodice, Outcome } from '@aulaviva/ui';
import { Search } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useVerificaAttestato } from '@/api/documenti';
import { formattaData } from '@/lib/date';
import { comuni, documenti } from '@/testi';

export function Verifica() {
  const t = documenti.verifica;
  const { codice: codiceUrl = '' } = useParams();
  const navigate = useNavigate();
  const codiceRichiesto = normalizzaCodice(codiceUrl);
  const [codice, setCodice] = useState(codiceRichiesto);
  const [errore, setErrore] = useState<string | null>(null);
  const verifica = useVerificaAttestato(codiceRichiesto.length === LUNGHEZZA_CODICE ? codiceRichiesto : null);

  useEffect(() => {
    setCodice(codiceRichiesto);
  }, [codiceRichiesto]);

  function invia(e: FormEvent) {
    e.preventDefault();
    if (codice.length !== LUNGHEZZA_CODICE) {
      setErrore(t.codiceNonValido);
      return;
    }
    setErrore(null);
    navigate(`/verifica/${codice}`);
  }

  const v = verifica.data;
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="t-title-1">{t.titolo}</h1>
        <p className="t-body text-ink-muted">{t.sottotitolo}</p>
      </div>
      <form onSubmit={invia} className="flex flex-col gap-4" noValidate>
        <CodiceVerificaField label={t.campo} hint={t.aiuto} value={codice} onValueChange={(c) => { setCodice(c); setErrore(null); }} error={errore ?? undefined} />
        <Button type="submit" variant="primary" icon={Search} loading={verifica.isFetching} loadingText={t.inCorso}>
          {t.azione}
        </Button>
      </form>
      {verifica.isError && <Alert variant="error" title={verifica.error.message} action={<Button onClick={() => verifica.refetch()}>{comuni.riprova}</Button>} />}
      {v && v.valido && (
        <Outcome variant="success" title={t.validoTitolo}>
          <p>{t.validoTesto}</p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 pt-3 t-body" data-verifica="valido">
            <dt className="t-label text-ink-muted">{t.campi.intestatario}</dt>
            <dd>
              {v.nome} {v.cognome}
            </dd>
            <dt className="t-label text-ink-muted">{t.campi.webinar}</dt>
            <dd>{v.webinarTitolo}</dd>
            {v.percorsoTitolo && (
              <>
                <dt className="t-label text-ink-muted">{t.campi.percorso}</dt>
                <dd>{v.percorsoTitolo}</dd>
              </>
            )}
            {v.dataWebinar && (
              <>
                <dt className="t-label text-ink-muted">{t.campi.data}</dt>
                <dd>{formattaData(v.dataWebinar)}</dd>
              </>
            )}
            {v.minutiTotali !== null && (
              <>
                <dt className="t-label text-ink-muted">{t.campi.presenza}</dt>
                <dd>{formattaMinuti(v.minutiTotali)}</dd>
              </>
            )}
            {v.esitoTest && (
              <>
                <dt className="t-label text-ink-muted">{t.campi.test}</dt>
                <dd>{t.campi.testSuperato}</dd>
              </>
            )}
            {v.emessoAt && (
              <>
                <dt className="t-label text-ink-muted">{t.campi.emesso}</dt>
                <dd>{formattaData(v.emessoAt)}</dd>
              </>
            )}
            <dt className="t-label text-ink-muted">{t.campi.codice}</dt>
            <dd>
              <code className="t-code">{formattaCodice(v.codice)}</code>
            </dd>
          </dl>
        </Outcome>
      )}
      {v && !v.valido && v.motivoNonValido === 'revocato' && (
        <Outcome variant="error" title={t.revocatoTitolo}>
          <p>{t.revocatoTesto}</p>
          {v.nome && (
            <p className="pt-2">
              {t.campi.intestatario}: {v.nome} {v.cognome}
              {v.webinarTitolo ? ` · ${v.webinarTitolo}` : ''}
            </p>
          )}
        </Outcome>
      )}
      {v && !v.valido && v.motivoNonValido !== 'revocato' && (
        <Outcome variant="error" title={t.nonTrovatoTitolo}>
          <p>{t.nonTrovatoTesto}</p>
        </Outcome>
      )}
    </div>
  );
}
